import { Alert, Form, Modal, Space, Tag, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useEffect } from "react";

import { toConfigurationFormValues } from "../model/configuration";
import type {
  LeadTimeConfigurationFormValues,
  LeadTimeRuleTarget,
} from "../model/types";
import { DeadlineRuleFields } from "./DeadlineRuleFields";

const { Text } = Typography;

interface EditRuleModalProps {
  open: boolean;
  loading: boolean;
  target?: LeadTimeRuleTarget;
  bulkCount?: number;
  onCancel: () => void;
  onSubmit: (values: LeadTimeConfigurationFormValues) => void;
}

export const EditRuleModal = ({
  open,
  loading,
  target,
  bulkCount,
  onCancel,
  onSubmit,
}: EditRuleModalProps) => {
  const [form] = Form.useForm<LeadTimeConfigurationFormValues>();
  const isBulk = typeof bulkCount === "number" && bulkCount > 0;

  useEffect(() => {
    if (!open) return;
    if (isBulk) {
      form.resetFields();
      form.setFieldsValue({ lead_time_days: 0, cutoff_extra_days: 0 });
    } else if (target) {
      form.setFieldsValue(toConfigurationFormValues(target));
    }
  }, [form, isBulk, open, target]);

  return (
    <Modal
      open={open}
      width={720}
      centered
      destroyOnHidden
      title={
        isBulk ? `${bulkCount}件のルールを一括変更` : "注文期限ルールを変更"
      }
      okText={isBulk ? `${bulkCount}件を更新する` : "この内容で更新"}
      cancelText="キャンセル"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => form.submit()}
    >
      {target && !isBulk && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
          message={
            <Space wrap>
              <Text strong>変更対象</Text>
              <Tag color="blue">{target.customer_code}</Tag>
              <Text>×</Text>
              <Tag color="geekblue">{target.product_code}</Tag>
              <Text>{target.product_name || "商品名なし"}</Text>
            </Space>
          }
          description="この行の注文期限設定を直接更新します。他の行には影響しません。"
        />
      )}
      {isBulk && (
        <Alert
          type="warning"
          showIcon
          icon={<EditOutlined />}
          style={{ marginBottom: 20 }}
          message={
            <Space>
              <Text strong>{bulkCount}件</Text>
              <Text>の注文期限ルールを一括変更します</Text>
            </Space>
          }
          description="選択したすべての行の注文期限設定を、同じ内容で直接更新します。"
        />
      )}
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        initialValues={{ lead_time_days: 0, cutoff_extra_days: 0 }}
        onFinish={onSubmit}
      >
        <DeadlineRuleFields form={form} />
      </Form>
    </Modal>
  );
};
